/**
 * Блоки вкладки под фильтром темы (#2229, спека §3) — одно правило на три
 * вкладки: числа рисуются, только когда фильтра нет или каждый суженный ответ
 * подтвердил тему эхом; ответ ещё в пути — загрузка; ответ без эха — ничего,
 * кроме блока тем: фильтр его не сужает (строка фильтра говорит «недоступен»,
 * #2238). Несравнимое прошлое окно темы гасит
 * сдвиги во всех блоках внутри (`VoiceComparableContext`). Тут же — окно
 * рядов блоков (#2394 §9 п. 7): окно строк вкладки.
 */
import type { ReactNode } from 'react';
import type { VoiceThemeFilterTab } from './useVoiceThemeFilter';
import { VoiceSeriesWindowContext, type VoiceSeriesWindow } from './useVoiceSeries';
import { VoiceComparableContext } from './voice-theme-filter-context';
import { VoiceLoading } from './VoiceLoading';

interface VoiceThemeFilterBlocksProps {
  filter: VoiceThemeFilterTab;
  /**
   * The window the tab's rows were counted for; `null` — a row still shows
   * the previous window's answer (a placeholder), and the series wait for it.
   */
  seriesWindow: VoiceSeriesWindow | null;
  children: ReactNode;
  /**
   * What the filter does not narrow — the themes block (§2.5): drawn in place
   * of the blocks when an answer came without the theme's echo, since its
   * numbers are the whole set's either way (#2238).
   */
  unnarrowed?: ReactNode;
}

export function VoiceThemeFilterBlocks({
  filter,
  seriesWindow,
  children,
  unnarrowed,
}: VoiceThemeFilterBlocksProps) {
  if (!filter.blocksShown && filter.verdict.kind === 'pending') return <VoiceLoading />;
  const shown = filter.blocksShown ? children : unnarrowed;
  if (!shown) return null;
  return (
    // The themes block alone is not narrowed: its window is comparable.
    <VoiceComparableContext.Provider
      value={filter.blocksShown ? filter.comparable : true}
    >
      <VoiceSeriesWindowContext.Provider value={seriesWindow}>
        {shown}
      </VoiceSeriesWindowContext.Provider>
    </VoiceComparableContext.Provider>
  );
}
